/**
 * Phase 1 read-only workflow graph viewer (Section 5).
 *
 *  - Fetches GET /api/v1/workflows/:id/graph
 *  - Lays out the result via dagre on the client (rankdir TB, nodesep 40, ranksep 60)
 *  - Renders with React Flow v12; nodes are non-draggable, edges non-connectable
 *  - Background dots only above zoom 0.6 (accessibility — L3 in the review)
 *  - Download PNG button: serialises the React Flow viewport to PNG via
 *    html-to-image, then POSTs to /workflows/:id/graph-export-audit so the
 *    server emits a `workflow.graph_exported` audit row (L5)
 *  - Empty-state when the workflow has no steps yet
 *
 * The component is intentionally self-contained so it can be embedded in
 * the editor's right-side sheet OR shown standalone for a deep-link.
 */

import { useCallback, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type NodeTypes,
  type Viewport,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toPng } from 'html-to-image';
import type { WorkflowGraph } from '@mana/shared';
import { api } from '../lib/api.js';
import { Button } from '../ui/Button.js';
import { layoutWorkflowGraph } from './layout.js';
import { TriggerNode } from './nodes/TriggerNode.js';
import { StepNode } from './nodes/StepNode.js';
import { EndNode } from './nodes/EndNode.js';

// Node-type registry. Defined once outside the component so React Flow
// doesn't re-mount custom nodes on every render.
const nodeTypes: NodeTypes = {
  trigger: TriggerNode,
  step: StepNode,
  end: EndNode,
};

// ---------------------------------------------------------------------------

export interface GraphViewProps {
  workflowId: string;
  /** Optional close handler so the parent sheet can wire its own X button. */
  onClose?: () => void;
}

export function GraphView({ workflowId, onClose }: GraphViewProps) {
  return (
    <ReactFlowProvider>
      <GraphViewInner workflowId={workflowId} onClose={onClose} />
    </ReactFlowProvider>
  );
}

function GraphViewInner({ workflowId, onClose }: GraphViewProps) {
  const graphQuery = useQuery<WorkflowGraph>({
    queryKey: ['workflows', 'graph', workflowId],
    queryFn: () => api<WorkflowGraph>(`/api/v1/workflows/${workflowId}/graph`),
    enabled: !!workflowId,
  });

  const laidOut = useMemo(() => {
    if (!graphQuery.data) return null;
    return layoutWorkflowGraph(graphQuery.data);
  }, [graphQuery.data]);

  // Track current zoom for the L3 background-visibility rule.
  const [zoom, setZoom] = useState(1);
  const onViewportChange = useCallback((vp: Viewport) => {
    setZoom(vp.zoom);
  }, []);

  // Capture the React Flow root DOM so we can rasterise it for PNG export.
  const rfWrapperRef = useRef<HTMLDivElement>(null);
  const rf = useReactFlow();
  const [exporting, setExporting] = useState(false);

  const handleDownloadPng = useCallback(async () => {
    if (!rfWrapperRef.current) return;
    setExporting(true);
    try {
      // Fit the view first so the export captures the whole graph.
      try {
        rf.fitView({ padding: 0.1, duration: 0 });
      } catch {
        // Pre-mount — ignore.
      }
      // The actual element we want is the .react-flow__viewport.
      const viewportEl =
        rfWrapperRef.current.querySelector<HTMLElement>('.react-flow__viewport') ??
        rfWrapperRef.current;
      const dataUrl = await toPng(viewportEl, {
        backgroundColor: '#FAF8F3',
        pixelRatio: 2,
        cacheBust: true,
      });
      // Trigger browser download.
      const link = document.createElement('a');
      link.download = `workflow-${workflowId}-graph.png`;
      link.href = dataUrl;
      link.click();
      // Fire-and-forget the audit POST. We don't block the download on it,
      // but we still want to know if it failed in dev.
      api(`/api/v1/workflows/${workflowId}/graph-export-audit`, {
        method: 'POST',
      }).catch((err: unknown) => {
        console.warn('graph-export-audit failed', err);
      });
    } catch (err) {
      console.error('PNG export failed', err);
    } finally {
      setExporting(false);
    }
  }, [rf, workflowId]);

  // ---------------------------------------------------------------------
  // Render states
  // ---------------------------------------------------------------------
  if (graphQuery.isLoading) {
    return (
      <div className="flex h-full min-h-[400px] items-center justify-center text-sm text-slate-500">
        Loading graph…
      </div>
    );
  }
  if (graphQuery.isError) {
    return (
      <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-2 p-6">
        <p className="text-sm font-medium text-rose-700">Couldn&apos;t load the graph.</p>
        <p className="text-xs text-slate-500">
          {(graphQuery.error as Error)?.message ?? 'Unknown error'}
        </p>
        {onClose && (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    );
  }
  if (!laidOut || laidOut.nodes.length === 0) {
    return (
      <div className="flex h-full min-h-[400px] flex-col items-center justify-center gap-3 p-6 text-center">
        <div className="font-display text-lg text-brand-navy-700">No graph yet</div>
        <p className="max-w-md text-sm text-slate-500">
          Add at least one trigger and one step in the editor and the graph will appear here.
        </p>
        {onClose && (
          <Button variant="secondary" size="sm" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-[400px] w-full flex-col">
      {/* Toolbar ----------------------------------------------------------- */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">
            Graph
          </span>
          <span className="text-xs text-slate-400">
            v{graphQuery.data?.version ?? '?'} · {laidOut.nodes.length} node
            {laidOut.nodes.length === 1 ? '' : 's'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={exporting}
            onClick={handleDownloadPng}
            title="Download a PNG snapshot of the current graph"
          >
            {exporting ? 'Exporting…' : 'Download PNG'}
          </Button>
          {onClose && (
            <Button variant="ghost" size="sm" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>

      {/* Canvas ------------------------------------------------------------ */}
      <div ref={rfWrapperRef} className="relative flex-1" style={{ background: '#FAF8F3' }}>
        <ReactFlow
          nodes={laidOut.nodes}
          edges={laidOut.edges}
          nodeTypes={nodeTypes}
          fitView
          minZoom={0.2}
          maxZoom={2}
          // Read-only: disable everything that would let a user mutate the graph.
          nodesDraggable={false}
          nodesConnectable={false}
          edgesFocusable={false}
          elementsSelectable={true}
          panOnDrag={true}
          zoomOnScroll={true}
          zoomOnPinch={true}
          onViewportChange={onViewportChange}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#FAF8F3' }}
        >
          {/* Background dots only above 0.6 zoom (L3 — accessibility). */}
          {zoom >= 0.6 && (
            <Background
              variant={BackgroundVariant.Dots}
              gap={20}
              size={1}
              color="#BFCCDC"
            />
          )}
          <MiniMap
            zoomable
            pannable
            maskColor="rgba(27, 58, 92, 0.06)"
            nodeColor={(n) => {
              if (n.type === 'trigger') return '#1B3A5C';
              if (n.type === 'end') return '#94A7BE';
              return '#B8965A';
            }}
            nodeStrokeColor="#1B3A5C"
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E9F1',
              borderRadius: 8,
            }}
          />
          <Controls
            showInteractive={false}
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #E2E9F1',
              borderRadius: 8,
              overflow: 'hidden',
            }}
          />
        </ReactFlow>
      </div>
    </div>
  );
}
